import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { CircularProgress } from '@mui/material';
import { toDisplayUrl, isUploadedRef } from './draftImageUtils';

const resolveImgSrc = (img) => {
  if (!img) return '';
  if (isUploadedRef(img)) return img.displayUrl || toDisplayUrl(img);
  if (typeof img === 'string') return img;
  if (img.base64_data) return img.base64_data;
  if (img.preview?.base64_data) return img.preview.base64_data;
  return toDisplayUrl(img);
};

const ImageGalleryModal = ({ isOpen, onClose, images, loading }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scale, setScale] = useState(1);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const goPrev = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setScale(1);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setScale(1);
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.4));
  const resetZoom = () => setScale(1);

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[90%] max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-700">
            Attached Images {images?.length > 0 && `(${activeIndex + 1} / ${images.length})`}
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-black transition">
            <CloseIcon />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <CircularProgress />
          </div>
        ) : images?.length > 0 ? (
          <>
            {/* Zoom Controls */}
            <div className="flex justify-center gap-4 py-2 border-b bg-white">
              <button onClick={zoomOut} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg">
                <RemoveIcon fontSize="small" />
              </button>
              <button onClick={resetZoom} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg">
                <RestartAltIcon fontSize="small" />
              </button>
              <button onClick={zoomIn} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg">
                <AddIcon fontSize="small" />
              </button>
            </div>

            {/* Image display with navigation arrows */}
            <div className="relative flex items-center justify-center min-h-[400px] max-h-[65vh] overflow-auto p-4">
              {images.length > 1 && (
                <button
                  onClick={goPrev}
                  className="absolute left-2 z-10 bg-white/80 hover:bg-white shadow-md rounded-full p-2"
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </button>
              )}

              <img
                src={resolveImgSrc(images[activeIndex])}
                alt={images[activeIndex]?.filename || `Image ${activeIndex + 1}`}
                className="rounded-lg shadow-md border object-contain max-h-[60vh] transition-transform duration-200"
                style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}
              />

              {images.length > 1 && (
                <button
                  onClick={goNext}
                  className="absolute right-2 z-10 bg-white/80 hover:bg-white shadow-md rounded-full p-2"
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </button>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 px-4 py-3 border-t bg-gray-50 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                {images.map((img, idx) => (
                  <img
                    key={idx}
                    src={resolveImgSrc(img)}
                    alt={img?.filename || `Thumb ${idx + 1}`}
                    onClick={() => { setActiveIndex(idx); setScale(1); }}
                    className={`w-16 h-16 object-cover rounded-lg cursor-pointer border-2 transition-all ${
                      idx === activeIndex ? 'border-[#1E4976] shadow-md' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-center py-16">No images available</p>
        )}
      </div>
    </div>
  );
};

export default ImageGalleryModal;
